import { useCallback, useEffect, useState } from "react";

const STORAGE_PREFIX = "fcl-report-grid-";

const readPrefs = (gridId) => {
    try {
        const raw = localStorage.getItem(`${STORAGE_PREFIX}${gridId}`);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const writePrefs = (gridId, prefs) => {
    try {
        localStorage.setItem(`${STORAGE_PREFIX}${gridId}`, JSON.stringify(prefs));
    } catch {
        /* ignore quota errors */
    }
};

/**
 * Persists column visibility and page size per report grid.
 */
export const useReportGridPreferences = (gridId, allColumnKeys = [], defaultPageSize = 10) => {
    const saved = readPrefs(gridId);

    const [visibleKeys, setVisibleKeys] = useState(
        () => saved?.visibleKeys?.filter((k) => allColumnKeys.includes(k)) ?? [...allColumnKeys]
    );
    const [pageSize, setPageSize] = useState(() => saved?.pageSize ?? defaultPageSize);

    useEffect(() => {
        if (!allColumnKeys.length) return;
        setVisibleKeys((prev) => {
            const valid = prev.filter((k) => allColumnKeys.includes(k));
            if (valid.length > 0) return valid;
            return [...allColumnKeys];
        });
    }, [allColumnKeys.join("|")]);

    useEffect(() => {
        if (!gridId) return;
        writePrefs(gridId, { visibleKeys, pageSize });
    }, [gridId, visibleKeys, pageSize]);

    const toggleColumn = useCallback((key) => {
        setVisibleKeys((prev) => {
            if (prev.includes(key)) {
                if (prev.length <= 1) return prev;
                return prev.filter((k) => k !== key);
            }
            return [...prev, key];
        });
    }, []);

    const showAllColumns = useCallback(() => setVisibleKeys([...allColumnKeys]), [allColumnKeys]);

    const resetPreferences = useCallback(() => {
        setVisibleKeys([...allColumnKeys]);
        setPageSize(defaultPageSize);
    }, [allColumnKeys, defaultPageSize]);

    return {
        visibleKeys,
        pageSize,
        setPageSize,
        toggleColumn,
        showAllColumns,
        resetPreferences,
    };
};
